import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Activity, Building2, Plus, Server as ServerIcon, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Card } from '../../../shared/components/ui/Card.js';
import { Modal } from '../../../shared/components/ui/Modal.js';
import { serversApi, type Server } from '../api.js';
import { ServerFormModal } from '../components/ServerFormModal.js';

export function ServersDashboard() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<Server | undefined>();
  const [deletingServer, setDeletingServer] = useState<Server | null>(null);
  const qc = useQueryClient();

  const deleteMut = useMutation({
    mutationFn: (s: Server) => serversApi.delete(s.id),
    onSuccess: () => {
      toast.success('Servidor removido');
      void qc.invalidateQueries({ queryKey: ['servers'] });
      setDeletingServer(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const serversQuery = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list(),
  });

  // Health check em batch (fan-out paralelo)
  const servers = serversQuery.data ?? [];
  const healthQueries = useQueries({
    queries: servers.map((s) => ({
      queryKey: ['server-health', s.id],
      queryFn: () => serversApi.health(s.id),
      retry: false,
      staleTime: 30_000,
    })),
  });

  const handleDelete = (s: Server): void => setDeletingServer(s);

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#0f172a]">Servidores</h1>
          <p className="text-sm text-[#64748b] mt-0.5">
            Gerencie servidores MetricsFarma e suas contas
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingServer(undefined);
            setModalOpen(true);
          }}
        >
          <Plus size={14} /> Adicionar Servidor
        </Button>
      </div>

      {serversQuery.isLoading && (
        <div className="py-12 text-center text-sm text-[#94a3b8]">Carregando...</div>
      )}

      {serversQuery.data?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#eff4ff] flex items-center justify-center mb-4">
            <ServerIcon size={24} stroke="#0a4ee3" strokeWidth={2} />
          </div>
          <p className="text-sm font-medium text-[#0f172a]">Nenhum servidor cadastrado</p>
          <p className="text-sm text-[#94a3b8] mt-1">
            Adicione seu primeiro servidor MetricsFarma pra começar a gerenciar contas.
          </p>
          <Button
            className="mt-4"
            onClick={() => {
              setEditingServer(undefined);
              setModalOpen(true);
            }}
          >
            <Plus size={14} /> Adicionar Servidor
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {servers.map((s, idx) => {
          const health = healthQueries[idx];
          const isUp = health?.data?.status === 200;
          const isChecking = health?.isFetching;
          const snapshot = health?.data ?? s.last_health_snapshot;

          return (
            <Card key={s.id} className="overflow-hidden transition-shadow hover:shadow-md">
              <Link
                to="/servidores/$id"
                params={{ id: String(s.id) }}
                className="block px-5 py-4"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2.5">
                    <div className="flex h-[36px] w-[36px] flex-shrink-0 items-center justify-center rounded-xl bg-[#eff4ff] text-[#0a4ee3]">
                      <ServerIcon size={18} strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-[#0f172a]">{s.name}</div>
                      <div className="truncate text-[11px] text-[#94a3b8] font-mono">{s.url}</div>
                    </div>
                  </div>
                  <span
                    className={`inline-flex flex-shrink-0 whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                      isUp
                        ? 'bg-emerald-50 text-emerald-700'
                        : isChecking
                          ? 'bg-[#eff4ff] text-[#0a4ee3]'
                          : 'bg-red-50 text-red-600'
                    }`}
                  >
                    {isChecking ? 'CHECANDO' : isUp ? 'ONLINE' : 'OFFLINE'}
                  </span>
                </div>

                {snapshot && 'counters' in snapshot && snapshot.counters && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-[#f1f5f9] pt-3">
                    <div className="flex items-center gap-1.5 text-xs text-[#64748b]">
                      <Users size={12} />
                      <span>
                        <strong className="font-semibold text-[#0f172a] tabular-nums">
                          {snapshot.counters.contas_total}
                        </strong>{' '}
                        contas
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-[#64748b]">
                      <Building2 size={12} />
                      <span>
                        <strong className="font-semibold text-[#0f172a] tabular-nums">
                          {snapshot.counters.empresas_total}
                        </strong>{' '}
                        empresas
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-[#64748b]">
                      <Activity size={12} />
                      <span>
                        <strong className="font-semibold text-[#0f172a] tabular-nums">
                          {snapshot.counters.usuarios_ativos}
                        </strong>{' '}
                        usuários
                      </span>
                    </div>
                  </div>
                )}
              </Link>

              <div className="flex items-center justify-end gap-1 border-t border-[#f1f5f9] bg-[#f8fafc] px-2 py-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingServer(s);
                    setModalOpen(true);
                  }}
                >
                  Editar
                </Button>
                <button
                  type="button"
                  onClick={() => void handleDelete(s)}
                  className="rounded-lg p-1.5 text-[#94a3b8] transition-colors hover:bg-red-50 hover:text-red-600"
                  title="Apagar"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      <ServerFormModal
        // key força re-mount quando troca de servidor — sem isso o useState
        // dos campos congela com os valores iniciais do PRIMEIRO render
        // (lição #87 do CLAUDE.md sobre useState(initial) congelado).
        key={editingServer?.id ?? 'new'}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingServer(undefined);
        }}
        server={editingServer}
      />

      {deletingServer && (
        <Modal open onClose={() => setDeletingServer(null)} title="Apagar servidor" size="md">
          <div className="space-y-4">
            <p className="text-xs text-[#94a3b8]">{deletingServer.name} — ação irreversível</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800">
              <p className="font-semibold mb-1">O que acontece:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Servidor removido do super (sem recuperação)</li>
                <li>A platform key cifrada é apagada — pra reconectar, gere outra</li>
                <li>Os dados do servidor MetricsFarma em si NÃO são afetados</li>
              </ul>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeletingServer(null)}
                disabled={deleteMut.isPending}
                className="px-4 py-2 text-sm font-medium text-[#64748b] hover:text-[#0f172a] rounded-lg hover:bg-[#f1f5f9] disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => deleteMut.mutate(deletingServer)}
                disabled={deleteMut.isPending}
                className="px-5 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg disabled:opacity-50"
              >
                {deleteMut.isPending ? 'Apagando...' : 'Apagar servidor'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
